<!--
  Writing a private note on selected lines (docs/design.md "Notes"). It says
  plainly that the note is the viewer's alone and is never posted. What is
  typed is kept in its box (state/drafts.ts) until the note is saved, so a
  reload keeps it.
-->
<script lang="ts">
  import { onMount } from 'svelte';
  import { commentKeys } from '../keys/keyboard.svelte';
  import { loadDraft, saveDraft } from '../state/drafts';
  import { noteBox, type NoteDraft } from '../state/fileNotes.svelte';

  interface Props {
    draft: NoteDraft;
    onsave: (text: string) => void;
    onclose: () => void;
  }
  let { draft, onsave, onclose }: Props = $props();

  // svelte-ignore state_referenced_locally
  let text = $state(draft.text ?? '');
  const box = $derived(noteBox(draft));
  const lines = $derived(draft.from === draft.to ? `line ${draft.from}` : `lines ${draft.from}–${draft.to}`);

  onMount(() => {
    if (text) return saveDraft(box, text);
    void loadDraft(box).then((saved) => {
      if (!text) text = saved;
    });
  });

  // Mod+Enter saves, Esc closes (the box keeps the text).
  const keys = commentKeys((c) => {
    if (c === 'submit') onsave(text);
    else onclose();
  });
</script>

<div class="composer note-composer">
  <div class="note-composer-head">
    <b>Note</b> on {draft.side === 'LEFT' ? 'old ' : ''}{lines} · <span class="note-private">only you see it; it is never posted</span>
  </div>
  <!-- svelte-ignore a11y_autofocus -->
  <textarea autofocus aria-label="Private note" placeholder="A note for yourself" bind:value={text} oninput={() => saveDraft(box, text)} {@attach keys}></textarea>
  <div class="row">
    {#if draft.failed}<span class="note-failed" role="alert">Not saved: {draft.failed}</span>{/if}
    <span class="spacer"></span>
    <button class="micro" onclick={onclose}>Cancel</button>
    <button class="micro primary" disabled={!text.trim()} onclick={() => onsave(text)}>Save note</button>
  </div>
</div>

<style>
  .note-composer {
    border-left: 3px solid var(--merged);
  }
  .note-composer-head {
    font-size: 0.85em;
    color: var(--muted);
    padding-bottom: 4px;
  }
  .note-private {
    color: var(--merged);
  }
  .note-failed {
    color: var(--bad);
    font-size: 0.85em;
  }
</style>
